/* eslint react/jsx-props-no-spreading: 0 */
import {
    Stack,
    Typography,
    Radio,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Box,
    Divider,
    FormHelperText,
    CircularProgress,
} from '@mui/material';
import {
    Accordion,
    Autocomplete,
    Checkbox,
    DatePicker,
    FormStepComponentProps,
    TextField,
    useCountries,
    useCountryAndStateOptions,
    useCurrency,
    useCurrencyOptions,
} from 'common';
import { useState, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CurrencyTypeMultiple } from '../../../constants';
import { BuyerSignupFormSchema } from '../BuyerSignUp.schema';
import { getMaxTrialEndDate, getMinTrialEndDate } from '../BuyerSignUp.utils';
import { useERPOptions, useERPSystems } from './../../../services/ERP/useERPSystems';
import { buyerSignUpFormText } from '../BuyerSignUp.constants';

const text = buyerSignUpFormText.generalInformation;

type GeneralInformationProps = FormStepComponentProps<typeof BuyerSignupFormSchema>;

export const GeneralInformation = ({ control }: GeneralInformationProps) => {
    // react-hook-form hooks
    const { setValue, getValues, clearErrors, getFieldState } = useFormContext(); // component wrapped with FormProvider in MultiStepForm.tsx

    function getIsSalesContactValid(): boolean {
        return (
            !getFieldState('generalInformation.contactSales').invalid &&
            getValues('generalInformation.contactSales.name') &&
            getValues('generalInformation.contactSales.phone') &&
            getValues('generalInformation.contactSales.email')
        );
    }

    const [hasSingleCurrency, setHasSingleCurrency] = useState<boolean>(
        getValues('generalInformation.currencyType') !== CurrencyTypeMultiple
    );

    const [isAdminSameAsSalesContact, setIsAdminSameAsSalesContact] = useState<boolean>(
        getValues('generalInformation.contactAdministrator.isSameAsSalesContact') ?? false
    );

    const [disableIsAdminSameAsSalesContact, setDisableIsAdminSameAsSalesContact] = useState<boolean>(
        !getIsSalesContactValid()
    );

    const minTrialEndDate = useMemo((): Date => {
        return getMinTrialEndDate();
    }, []);

    const maxTrialEndDate = useMemo((): Date => {
        return getMaxTrialEndDate();
    }, []);

    const selectedCountryCode =
        useWatch({ name: 'generalInformation.buyerCompanyAddress.country', control, exact: true }) ?? null;

    const { data: countryList = [], isLoading: isCountriesLoading } = useCountries();
    const { countryOptions, stateOptions } = useCountryAndStateOptions({ selectedCountryCode, countryList });

    const { data: currencyList = [], isLoading: isCurrencyLoading } = useCurrency();
    const currencyOptions = useCurrencyOptions(currencyList);

    const { data: erpList = [], isLoading: isERPSystemsLoading } = useERPSystems();
    const erpOptions = useERPOptions(erpList);

    function handleCountrySelect(val: string | null) {
        clearErrors('generalInformation.buyerCompanyAddress.state');
        setValue('generalInformation.buyerCompanyAddress.country', val, { shouldDirty: true });
        setValue('generalInformation.buyerCompanyAddress.state', '', { shouldDirty: true });
    }

    function handleHasSingleCurrencyClick(val: boolean) {
        setHasSingleCurrency(val);
        if (val === true) {
            setValue('generalInformation.currencyType', null, { shouldDirty: true });
        } else {
            // TODO: use value from defined enum
            clearErrors('generalInformation.currencyType');
            setValue('generalInformation.currencyType', CurrencyTypeMultiple, { shouldDirty: true });
        }
    }

    function handleIsAdminSameAsSalesContactCheck(val: boolean) {
        setIsAdminSameAsSalesContact(val);
        if (val === true) {
            const salesContact = getValues('generalInformation.contactSales');
            clearErrors('generalInformation.contactAdministrator');
            setValue(
                'generalInformation.contactAdministrator',
                {
                    isSameAsSalesContact: true,
                    name: salesContact.name,
                    phone: salesContact.phone,
                    email: salesContact.email,
                },
                { shouldDirty: true }
            );
        } else {
            setValue(
                'generalInformation.contactAdministrator',
                { isSameAsSalesContact: false, name: '', phone: '', email: '' },
                { shouldDirty: false }
            );
        }
    }

    // TODO: refactor these into single handler
    function handleUpdateSalesContact() {
        setIsAdminSameAsSalesContact(false);
        setValue(
            'generalInformation.contactAdministrator',
            { isSameAsSalesContact: false, name: '', phone: '', email: '' },
            { shouldDirty: false }
        );
    }

    function handleOnKeyUpSalesContact() {
        if (getFieldState('generalInformation.contactSales').isTouched && getIsSalesContactValid()) {
            setDisableIsAdminSameAsSalesContact(false);
        } else {
            setDisableIsAdminSameAsSalesContact(true);
        }
    }

    // sections
    const buyerCompanyNameSection = (
        <Box data-testid="buyerCompanyNameSection" width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.buyerCompanyName.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="generalInformation.buyerCompanyName"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    testId="enter-buyer-company-name"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    label="Buyer Company Name"
                                    required
                                />
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const buyerCompanyAddressSection = (
        <Box data-testid="buyerCompanyAddressSection" width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.buyerCompanyAddress.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="generalInformation.buyerCompanyAddress.addressLine1"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Address Line 1"
                                    testId="enter-buyer-company-address-line-1"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    required
                                />
                            )}
                        />
                        <Controller
                            name="generalInformation.buyerCompanyAddress.addressLine2"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Address Line 2"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                        <Controller
                            name="generalInformation.buyerCompanyAddress.addressLine3"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Address Line 3"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                        <Controller
                            name="generalInformation.buyerCompanyAddress.city"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="City"
                                    testId="enter-buyer-company-address-city"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    required
                                />
                            )}
                        />
                        {!Object.keys(stateOptions).length ? (
                            <Controller
                                name="generalInformation.buyerCompanyAddress.state"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        label="State/Province/ Territory"
                                        testId="enter-buyer-company-address-state-enter"
                                        {...field}
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message}
                                        required
                                    />
                                )}
                            />
                        ) : (
                            <Controller
                                name="generalInformation.buyerCompanyAddress.state"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Autocomplete
                                        id="State/Province/Territory"
                                        label="State/Province/ Territory"
                                        testId="enter-buyer-company-address-state-select"
                                        options={stateOptions}
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message}
                                        value={field.value}
                                        onChange={field.onChange}
                                        required
                                    />
                                )}
                            />
                        )}
                        <Controller
                            name="generalInformation.buyerCompanyAddress.zipCode"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="ZIP/Postal Code"
                                    testId="enter-buyer-company-address-zipcode"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    required
                                />
                            )}
                        />
                        {isCountriesLoading ? (
                            <CircularProgress size="20px" />
                        ) : (
                            <Controller
                                name="generalInformation.buyerCompanyAddress.country"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Autocomplete
                                        id="Country"
                                        label="Country"
                                        testId="enter-buyer-company-address-country"
                                        onChange={handleCountrySelect}
                                        options={countryOptions}
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message}
                                        value={field.value}
                                        required
                                        disableSorting
                                    />
                                )}
                            />
                        )}
                        <Controller
                            name="generalInformation.buyerCompanyAddress.phone"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Phone"
                                    testId="enter-buyer-company-address-phone"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    required
                                />
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const salesContactSection = (
        <Box data-testid="salesContactSection" width="100%">
            {/* If sales contact touched, uncheck (same as salesContact) */}
            <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                spacing={2}
                onClick={handleUpdateSalesContact}
            >
                <Accordion
                    title={text.sections.salesContact.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="generalInformation.contactSales.name"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Name"
                                    testId="enter-sales-contact-name"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    required
                                    onKeyUp={handleOnKeyUpSalesContact}
                                />
                            )}
                        />
                        <Controller
                            name="generalInformation.contactSales.phone"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Phone"
                                    testId="enter-sales-contact-phone"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    required
                                    onKeyUp={handleOnKeyUpSalesContact}
                                />
                            )}
                        />
                        <Controller
                            name="generalInformation.contactSales.email"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Email"
                                    testId="enter-sales-contact-email"
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    required
                                    onKeyUp={handleOnKeyUpSalesContact}
                                />
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const adminContactSection = (
        <Box data-testid="adminContactSection" width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.adminContact.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Checkbox
                            label="Same as Sales Contact"
                            testId="check-same-as-sales-contact"
                            checked={isAdminSameAsSalesContact}
                            onChange={(e) => handleIsAdminSameAsSalesContactCheck(e.target.checked)}
                            disabled={disableIsAdminSameAsSalesContact}
                        />
                        {isAdminSameAsSalesContact || (
                            <>
                                <Controller
                                    name="generalInformation.contactAdministrator.name"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            label="Name"
                                            testId="enter-admin-contact-name"
                                            {...field}
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                            disabled={isAdminSameAsSalesContact}
                                            required
                                        />
                                    )}
                                />
                                <Controller
                                    name="generalInformation.contactAdministrator.phone"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            label="Phone"
                                            testId="enter-admin-contact-phone"
                                            {...field}
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                            disabled={isAdminSameAsSalesContact}
                                            required
                                        />
                                    )}
                                />
                                <Controller
                                    name="generalInformation.contactAdministrator.email"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            label="Email"
                                            testId="enter-admin-contact-email"
                                            {...field}
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                            disabled={isAdminSameAsSalesContact}
                                            required
                                        />
                                    )}
                                />
                            </>
                        )}
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const erpSection = (
        <Box data-testid="erpSection" width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.erp.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        {isERPSystemsLoading ? (
                            <CircularProgress size="20px" />
                        ) : (
                            <Controller
                                name="generalInformation.erpSystemId"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Autocomplete
                                        id="ERPSystem"
                                        label="ERP System"
                                        testId="enter-erp-system"
                                        options={erpOptions}
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message}
                                        value={field.value}
                                        onChange={field.onChange}
                                        required
                                    />
                                )}
                            />
                        )}
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const maxSuppliersSection = (
        <Box data-testid="maxSuppliersSection" width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.maxSuppliers.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="generalInformation.maxSuppliers"
                            control={control}
                            render={({ field, fieldState }) => (
                                // @ts-ignore
                                <TextField
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    label="Maximum Number of Suppliers"
                                    numericOnly
                                />
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const maxInvoicesSection = (
        <Box data-testid="maxInvoicesSection" width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.maxInvoices.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="generalInformation.maxInvoices"
                            control={control}
                            render={({ field, fieldState }) => (
                                // @ts-ignore
                                <TextField
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    label="Maximum Number of Invoices"
                                    numericOnly
                                />
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const oneCurrencySection = (
        <Box data-testid="oneCurrencySection" width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.oneCurrency.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="generalInformation.currencyType"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth error={fieldState.invalid}>
                                    <RadioGroup
                                        value={hasSingleCurrency}
                                        onChange={(e) => handleHasSingleCurrencyClick(JSON.parse(e.target.value))}
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <FormControlLabel
                                                value={true}
                                                control={<Radio data-testid="select-currency-radio-single" />}
                                                label="Only one currency"
                                                sx={{ width: '40%' }}
                                            />
                                            {hasSingleCurrency &&
                                                (isCurrencyLoading ? (
                                                    <CircularProgress size="20px" />
                                                ) : (
                                                    <Autocomplete
                                                        id="CurrencyType"
                                                        label=""
                                                        testId="select-currency-select-type"
                                                        options={currencyOptions}
                                                        error={fieldState.invalid}
                                                        helperText={fieldState.error?.message}
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        required
                                                        disableSorting
                                                    />
                                                ))}
                                        </Stack>
                                        <FormControlLabel
                                            value={false}
                                            control={<Radio data-testid="select-currency-radio-multiple" />}
                                            label="Multiple currencies"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const trialEndDateSection = (
        <Box data-testid="trialEndDateSection" width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.trialEndDate.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="generalInformation.trialEndDate"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl error={fieldState.invalid} fullWidth>
                                    <DatePicker
                                        {...field}
                                        error={fieldState.invalid}
                                        minDate={minTrialEndDate}
                                        maxDate={maxTrialEndDate}
                                        disablePast
                                        label="Trial End Date"
                                        required
                                    />
                                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    return (
        <>
            <Box className="GeneralInformation" width="100%">
                <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={4}
                    divider={<Divider orientation="horizontal" flexItem />}
                >
                    <Typography variant="h1" padding={2}>
                        {text.topHeader}
                    </Typography>
                    {buyerCompanyNameSection}
                    {buyerCompanyAddressSection}
                    {salesContactSection}
                    {adminContactSection}
                    {erpSection}
                    {maxSuppliersSection}
                    {maxInvoicesSection}
                    {oneCurrencySection}
                    {trialEndDateSection}
                </Stack>
            </Box>
        </>
    );
};
